/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { dropTask } from 'ember-concurrency';
import { computed } from '@ember/object';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import { FormType } from 'cio/utils/forms';
import SqlImportModel from 'cio/models/sql-import';
import CustomFormsService from 'cio/services/custom-forms';
import { ActivityLogSourceType } from 'cio/utils/activity-log-source-types';
import { isEmpty } from '@ember/utils';
import LogModel from 'cio/models/log';
import { ActivityLogSource, ActivityLogSourceClientType } from 'cio/utils/activity-logs';
import { action } from '@ember/object';
import { announceHydraNavigate } from '@customerio/hydra-nav-state/testing';
import { FlyMouseEvent } from '@customerio/fly-components/types/events';

const NO_PIPELINES_SOURCE_NAME = 'this source';

type FormattedLogSourceArgs = {
  log: LogModel;
  source: ActivityLogSource;
};

interface PipelinesUserAgentExtraData {
  workspaceId?: string;
  sourceId?: string;
  sourceName?: string;
  ip?: string;
  userAgent?: string;
}

function parsePipelinesUserAgent(ua: string) {
  let extraDataIndex = ua?.indexOf('{');
  let extraData = extraDataIndex > -1 ? ua?.substring(extraDataIndex) : '{}';
  let parsedData;
  try {
    parsedData = JSON.parse(extraData ?? '{}') as PipelinesUserAgentExtraData;
  } catch {
    // not a valid JSON, ignore it
    parsedData = {};
  }

  let pipelinesUserAgent = extraDataIndex > -1 ? ua?.substring(0, extraDataIndex).trim() : ua;
  return {
    pipelinesUserAgent,
    ...parsedData,
  };
}

export default class FormattedLogSource extends Component<FormattedLogSourceArgs> {
  @service store!: DS.Store;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;

  @tracked sqlImport?: SqlImportModel;

  readonly FormType = FormType;

  ActivityLogSourceType = ActivityLogSourceType;

  get facebookLeadAdsForm() {
    return (
      this.args.source.formType === FormType.FacebookLeadAds &&
      this.facebookLeadAds.formsByFormID[this.args.source.formId]
    );
  }

  @computed('args.source.{formType,formId}', 'customForms.forms.[]')
  get customForm() {
    return this.customForms.forms.find(
      (f) => f.formType === this.args.source.formType && f.formId === this.args.source.formId,
    );
  }

  get hasAction() {
    return (
      !isEmpty(this.args.source.actionType) ||
      !isEmpty(this.args.source.actionName) ||
      !isEmpty(this.args.source.actionId)
    );
  }

  get isSDK() {
    let clientType = this.args.source.client?.type;
    return [ActivityLogSourceClientType.iOS, ActivityLogSourceClientType.Android].includes(clientType);
  }

  get deviceType() {
    if (this.isSDK) {
      return this.args.source.client?.type === ActivityLogSourceClientType.iOS ? 'iOS' : 'Android';
    }
    return '';
  }

  get userAgent() {
    let { ua } = this.args.source;

    if (!this.isDataPipelines) {
      return ua;
    }

    let parsed = this.cdpAutoTrackExtraData;
    if (parsed.userAgent != null) {
      return `${parsed.userAgent}`;
    }
    return parsed.pipelinesUserAgent;
  }

  get pipelinesSourceName() {
    if (!this.isDataPipelines) {
      return NO_PIPELINES_SOURCE_NAME;
    }
    let { sourceName } = this.cdpAutoTrackExtraData;
    return sourceName != null && sourceName.length > 0 ? sourceName : NO_PIPELINES_SOURCE_NAME;
  }

  get cdpAutoTrackExtraData() {
    let { ua } = this.args.source;
    return parsePipelinesUserAgent(ua);
  }

  get isDataPipelines() {
    let { ua } = this.args.source;
    return ua?.toLowerCase().startsWith('customer.io data pipelines');
  }

  get hasDataPipelinesSourceMeta() {
    let { workspaceId, sourceId } = this.cdpAutoTrackExtraData;
    return workspaceId != null && sourceId != null;
  }

  constructor(owner: unknown, args: FormattedLogSourceArgs) {
    super(owner, args);

    if (this.args.source.type === ActivityLogSourceType.FormIntegration) {
      if (this.args.source.formType === FormType.FacebookLeadAds) {
        this.facebookLeadAds.initialize();
      } else {
        this.customForms.initialize();
      }
    } else if (this.args.source.type === ActivityLogSourceType.SQLImport) {
      this.fetchSqlImportTask.perform();
    }
  }

  fetchSqlImportTask = dropTask(async () => {
    try {
      this.sqlImport = await this.store.findRecord('sql-import', this.args.source.importId);
    } catch {}
  });

  get pipelinesSourceUrl() {
    let { workspaceId, sourceId } = this.cdpAutoTrackExtraData;
    if (workspaceId == null || sourceId == null) {
      return '#';
    }
    let safeWorkspaceId = parseInt(workspaceId);
    let safeSourceId = parseInt(sourceId);
    if (isNaN(safeWorkspaceId) || isNaN(safeSourceId)) {
      return '#';
    }
    return `/workspaces/${workspaceId}/pipelines/sources/${sourceId}/data-in`;
  }

  @action
  navigateToSourceInDataPipelines(event: FlyMouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    let url = this.pipelinesSourceUrl;
    if (url !== '#') {
      announceHydraNavigate(url, { targetApp: 'pipelines' });
    }
  }
}
